import React, { useState, useEffect } from "react";
import {
    Button,
    Dropdown,
    Field,
    Input,
    Option,
    Switch,
    makeStyles,
} from "@fluentui/react-components";
import {
    Add20Regular,
    Delete20Regular,
} from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";

import AddRoutingDialog from "./CallFlowDiagram/AddRoutingDialog";
import IvrComponent from "./Routing/IvrComponent";
import PromptContainer from "./Routing/PromptContainer";
import RegexpComponent from "./Routing/RegexpComponent";
import TimeComponent from "./Routing/TimeComponent";
import DaysComponent from "./Routing/DaysComponent";
import CheckVariableComponent from "./Routing/CheckVariableComponent";
import QueueSelectorComponent from "../GenericComponent/QueueSelectorComponent";
import InfoButton from "../GenericComponent/InfoButton";
import { ConfirmAlert } from "../GenericComponent/ConfirmAlert";
import "../CSS/RoutingComponent.css";

const useStyles = makeStyles({
    container: {
        display: "flex",
        gap: "1rem",
    },
    leftPanel: {
        width: "30%",
        borderRight: "1px solid #ccc",
        paddingRight: "1rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.5rem",
        maxHeight: "70vh",
        overflowY: "auto",
    },
    routingItem: {
        padding: "0.5rem",
        cursor: "pointer",
        borderRadius: "4px",
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        "&:hover": { backgroundColor: "#eee" },
    },
    selectedRouting: {
        backgroundColor: "#ddd",
        fontWeight: 600,
    },
    rightPanel: {
        width: "70%",
        paddingLeft: "1rem",
    },
    addRow: {
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        marginBottom: "1rem",
    },
    editingTitle: {
        fontWeight: 600,
        fontSize: "1.2rem",
        marginBottom: "1rem",
    },
    // L'onglet est un conteneur `display: flex` dont CHAQUE enfant direct reçoit `width: 85%`
    // (CSS/CallFlowsComponent.css). Rendre le bandeau et les panneaux côte à côte en ferait donc
    // deux colonnes de 85 % : le réglage se retrouvait tassé à gauche, sous la liste. D'où cet
    // enveloppement — un seul enfant, empilé en colonne.
    page: {
        display: "flex",
        flexDirection: "column",
        width: "100%",
        minWidth: 0,
    },
    flowSettings: {
        display: "flex",
        alignItems: "center",
        gap: "2rem",
        flexWrap: "wrap",
        padding: "0.75rem 1rem",
        marginBottom: "1rem",
        borderRadius: "6px",
        backgroundColor: "#f5f5f5",
        border: "1px solid #e0e0e0",
    },
    windowField: {
        maxWidth: "9rem",
    },
});

/** Fenêtre appliquée par le moteur quand le flux n'en fournit pas (Services/LastAgent.js). */
const LAST_AGENT_DEFAULT_WINDOW_DAYS = 7;

const ROUTING_TYPES = [
    "Queue",
    "Disconnect",
    "IVR",
    "Prompt",
    "Regexp",
    "Time",
    "Check days",
    "Check variable",
];

export default function RoutingComponent({ callFlowData, setCallFlowData, ACTIVE_TENANT, selectedRouting = null, permissions, isEditMode }) {
    const { t } = useTranslation();
    const classes = useStyles();


    const [routingToDelete, setRoutingToDelete] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const routingMap = callFlowData.routing || {};

    const [selectedRoutingName, setSelectedRoutingName] = useState(() => {
        const initial = (() => {
            if (selectedRouting && routingMap[selectedRouting]) return selectedRouting;
            const routingKeys = Object.keys(routingMap);
            if (routingKeys.includes("0")) return "0";
            return routingKeys[0] || null;
        })();
        return initial;
    });


    // Mettre à jour la sélection quand selectedRouting change
    useEffect(() => {
        if (selectedRouting && routingMap[selectedRouting]) {
            setSelectedRoutingName(selectedRouting);
        }
    }, [selectedRouting, routingMap]);

    // LAST AGENT ROUTING — réglage du FLUX, pas d'une étape de routage.
    //
    // Il ne dépend d'aucun nœud : il dit que l'appelant déjà traité sur CE flux au cours des N
    // derniers jours est proposé en priorité au conseiller qui l'avait pris, quelle que soit la
    // file où il atterrit. D'où sa place au-dessus de la liste des étapes, et non dedans.
    //
    // Le moteur lit exactement `lastAgentRouting.enabled === true` (FlowEngineUnify
    // Services/LastAgent.js, `settingsOf`) : tant que l'interrupteur est fermé, aucune requête
    // n'est faite au CDR et le routage ne change pas d'un iota.
    const lastAgentRouting = callFlowData.lastAgentRouting || {};
    const lastAgentEnabled = lastAgentRouting.enabled === true;
    const routingDisabled = isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate;

    const updateLastAgentRouting = (patch) => {
        setCallFlowData((prev) => ({
            ...prev,
            lastAgentRouting: { ...(prev.lastAgentRouting || {}), ...patch },
        }));
    };

    const updateRouting = (routingName, updater) => {
        setCallFlowData((prev) => {
            const routing = { ...(prev.routing || {}) };
            const current = routing[routingName] || {};
            routing[routingName] =
                typeof updater === "function" ? updater(current) : updater;
            return { ...prev, routing };
        });
    };

    const handleRoutingTypeChange = (routingName, value) => {
        let type = value.toLowerCase();
        if (type === "check variable") type = "check_variable";
        if (type === "check days") type = "check_days";

        updateRouting(routingName, (node) => {
            const updated = { ...node, type };
            if (type === "ivr" || type === "prompt") {
                updated.audio = { ...(updated.audio || {}), type: "text2speech" };
            }
            return updated;
        });
    };

    const handleFieldChange = (routingName, field, value) => {
        updateRouting(routingName, (node) => {
            const updated = { ...node };
            const parts = field.split(".");
            let obj = updated;
            parts.slice(0, -1).forEach((k) => {
                obj[k] = obj[k] || {};
                obj = obj[k];
            });
            obj[parts[parts.length - 1]] = value;
            return updated;
        });
    };

    const addRouting = (name) => {
        if (!name || routingMap[name]) return;

        const baseNode = {
            enabled: true,
            displayName: name,
            type: "queue",
            queueDisplayName: "",
            queueId: "",
            expectedDigitNumber: 1,
            lastTone: "#",
            timeout: 10,
            audio: {
                type: "text2speech",
                language: "",
                text: "",
                audioFileUrl: "",
            },
            options: {},
            next: { type: "" },
        };

        updateRouting(name, baseNode);
        setIsDialogOpen(false);
        setSelectedRoutingName(name);
    };

    const deleteRouting = (name) => {
        setCallFlowData((prev) => {
            const routing = { ...(prev.routing || {}) };
            delete routing[name];
            return { ...prev, routing };
        });
        setRoutingToDelete(null);
        if (selectedRoutingName === name) setSelectedRoutingName(null);
    };

    const setOnMatched = (routingName, payload) => {
        updateRouting(routingName, (node) => ({
            ...node,
            onMatched: { ...(node.onMatched || {}), ...payload },
        }));
    };

    const setOnUnMatched = (routingName, payload) => {
        updateRouting(routingName, (node) => ({
            ...node,
            onUnMatched: { ...(node.onUnMatched || {}), ...payload },
        }));
    };

    const getOtherRoutings = (currentRoutingName) => {
        return Object.keys(routingMap)
            .filter((rName) => rName !== currentRoutingName)
            .map((rName) => ({
                routingName: rName,
                displayName: rName === "0" ? "Start" : routingMap[rName].displayName || rName,
            }));
    };

    const displayRoutingName = (name) => (name === "0" ? "Start" : routingMap[name].displayName || name);

    const displayRoutingWithType = (name) => {
        const routing = routingMap[name];
        const type = routing?.type || "queue";
        return `${displayRoutingName(name)} (${type})`;
    };

    return (
        <>
            {/* Add Routing Dialog */}
            <AddRoutingDialog
                isOpen={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                onAdd={addRouting}
                existingRoutingNames={Object.keys(routingMap)}
            />

            <div className={classes.page}>
            {/* Réglage de flux : bandeau horizontal en tête, puis les étapes de routage */}
            <div className={classes.flowSettings}>
                <Field
                    label={
                        <span>
                            {t("LAST_AGENT_ROUTING")}{" "}
                            <InfoButton infoKey="LAST_AGENT_ROUTING" category="CALL_FLOWS" />
                        </span>
                    }
                >
                    <div className="switch-wrapper">
                        <Switch
                            checked={lastAgentEnabled}
                            disabled={routingDisabled}
                            onChange={(_, data) =>
                                updateLastAgentRouting({
                                    enabled: data.checked,
                                    // On matérialise la fenêtre dès l'activation : le champ ne doit
                                    // pas rester vide en affichant implicitement un défaut moteur
                                    // que personne ne voit.
                                    windowDays:
                                        Number(lastAgentRouting.windowDays) > 0
                                            ? Number(lastAgentRouting.windowDays)
                                            : LAST_AGENT_DEFAULT_WINDOW_DAYS,
                                })
                            }
                        />
                    </div>
                </Field>

                {lastAgentEnabled && (
                    <Field label={t("LAST_AGENT_ROUTING_WINDOW_DAYS")} className={classes.windowField}>
                        <Input
                            type="number"
                            min={1}
                            max={90}
                            value={
                                lastAgentRouting.windowDays === undefined || lastAgentRouting.windowDays === null
                                    ? ""
                                    : String(lastAgentRouting.windowDays)
                            }
                            disabled={routingDisabled}
                            // Vidé, ou hors bornes : on retire la valeur plutôt que d'écrire un
                            // zéro. Le moteur retombe alors sur sa fenêtre par défaut, au lieu de
                            // chercher sur zéro jour — c'est-à-dire de ne jamais rien trouver.
                            onChange={(_, data) => {
                                const days = parseInt(data.value, 10);
                                updateLastAgentRouting({
                                    windowDays: Number.isFinite(days) && days > 0 ? Math.min(days, 90) : undefined,
                                });
                            }}
                        />
                    </Field>
                )}
            </div>

            <div className={classes.container}>
                {/* Left panel: routing list */}
                <div className={classes.leftPanel}>
                    {Object.keys(routingMap).map((routingName) => {
                        const isSelected = routingName === selectedRoutingName;
                        return (
                            <div
                                key={routingName}
                                className={`${classes.routingItem} ${isSelected ? classes.selectedRouting : ""
                                    }`}
                            >
                                <span 
                                    style={{ flex: 1, cursor: 'pointer' }}
                                    onClick={() => setSelectedRoutingName(routingName)}
                                >
                                    {displayRoutingWithType(routingName)}
                                </span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    {isSelected ? (
                                        <span>☑️</span>
                                    ) : null}
                                    {routingName !== '0' && (
                                        <Button
                                            appearance="subtle"
                                            icon={<Delete20Regular />}
                                            size="small"
                                            disabled={isEditMode ? !permissions?.routing?.canDelete : !permissions?.routing?.canCreate}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setRoutingToDelete(routingName);
                                            }}
                                            style={{ minWidth: 'auto', padding: '4px' }}
                                        />
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    {/* Add Routing Button — placé APRÈS le dernier routage. Style discret
                        (subtle) avec le "+" en violet plutôt qu'un bouton plein foncé. */}
                    <Button
                        appearance="subtle"
                        icon={<Add20Regular />}
                        onClick={() => setIsDialogOpen(true)}
                        disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
                        style={{ marginTop: '0.5rem', color: 'var(--colorBrandForeground1)', fontSize: '0.8rem', fontWeight: 400 }}
                    >
                        {t("addRoutingStep", { defaultValue: "Add a routing step" })}
                    </Button>
                </div>

                {/* Right panel: selected routing details */}
                <div className={classes.rightPanel}>
                    {selectedRoutingName && (() => {
                        const routing = routingMap[selectedRoutingName];
                        const rawType = routing.type || "queue";
                        const displayType =
                            rawType === "check_variable"
                                ? t("checkVariable")
                                : t(`${rawType}`, rawType);

                        return (
                            <>
                                {/* Title */}
                                <div className={classes.editingTitle}>
                                    {displayRoutingName(selectedRoutingName)} - Editing
                                </div>

                                {/* Routing Name - Pour tous les types sauf IVR, Prompt et Start (0) */}
                                {rawType !== 'ivr' && rawType !== 'prompt' && selectedRoutingName !== '0' && (
                                    <div className="routing-row">
                                        <label className="routing-label-inline">{t("routingName")}</label>
                                        <Input
                                            className="routing-dropdown-flex"
                                            value={routing.displayName || routing.routingName || ''}
                                            disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
                                            onChange={(_, data) => 
                                                handleFieldChange(selectedRoutingName, 'displayName', data.value)
                                            }
                                            placeholder={t("enterRoutingName")}
                                        />
                                    </div>
                                )}

                                <div className="routing-row">
                                    <label className="routing-label-inline">{t("routingType")}</label>
                                    <Dropdown
                                        className="routing-dropdown-flex"
                                        placeholder={t("selectRoutingType")}
                                        value={displayType}
                                        disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
                                        onOptionSelect={(_, data) =>
                                            handleRoutingTypeChange(
                                                selectedRoutingName,
                                                data.optionValue || data.optionText
                                            )
                                        }
                                    >
                                        {ROUTING_TYPES.map((tKey) => (
                                            <Option key={tKey} value={tKey}>
                                                {t(tKey.toLowerCase().replace(/ /g, "_"), tKey)}
                                            </Option>
                                        ))}
                                    </Dropdown>
                                </div>

                                {/* Conditional rendering by type */}
                                {(() => {
                                    switch (rawType) {
                                        case "queue":
                                            return (
                                                <div className="routing-row">
                                                    <label className="routing-label-inline">{t("queue")}</label>
                                                    <div className="routing-flex-item">
                                                        <QueueSelectorComponent
                                                            displayName={routing.queueDisplayName || ""}
                                                            disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
                                                            getData={(queue) => {
                                                                handleFieldChange(
                                                                    selectedRoutingName,
                                                                    "queueDisplayName",
                                                                    queue.displayName
                                                                );
                                                                handleFieldChange(selectedRoutingName, "queueId", queue.id);
                                                                handleFieldChange(selectedRoutingName, "type", "queue");
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        case "ivr":
                                            return (
                                                <IvrComponent
                                                    callFlowData={callFlowData}
                                                    data={routing}
                                                    routingName={selectedRoutingName}
                                                    ACTIVE_TENANT={ACTIVE_TENANT}
                                                    permissions={permissions}
                                                    isEditMode={isEditMode}
                                                    onChange={handleFieldChange}
                                                    onAddOption={(key, option) =>
                                                        updateRouting(selectedRoutingName, (node) => ({
                                                            ...node,
                                                            options: { ...(node.options || {}), [key]: option },
                                                        }))
                                                    }
                                                    onRemoveOption={(key) =>
                                                        updateRouting(selectedRoutingName, (node) => {
                                                            const opts = { ...(node.options || {}) };
                                                            delete opts[key];
                                                            return { ...node, options: opts };
                                                        })
                                                    }
                                                    allRoutings={getOtherRoutings(selectedRoutingName)}
                                                />
                                            );
                                        case "prompt":
                                            return (
                                                <PromptContainer
                                                    callFlowData={callFlowData}
                                                    data={routing}
                                                    routingName={selectedRoutingName}
                                                    permissions={permissions}
                                                    isEditMode={isEditMode}
                                                    onChange={handleFieldChange}
                                                    allRoutings={routingMap}
                                                    ACTIVE_TENANT={ACTIVE_TENANT}
                                                />
                                            );
                                        case "regexp":
                                            return (
                                                <RegexpComponent
                                                    data={routing}
                                                    routingName={selectedRoutingName}
                                                    permissions={permissions}
                                                    isEditMode={isEditMode}
                                                    onChange={handleFieldChange}
                                                    setOnMatched={(val) => setOnMatched(selectedRoutingName, val)}
                                                    setOnUnMatched={(val) => setOnUnMatched(selectedRoutingName, val)}
                                                    routingList={routingMap}
                                                />
                                            );
                                        case "time":
                                            return (
                                                <TimeComponent
                                                    routingList={routingMap}
                                                    data={routing}
                                                    routingName={selectedRoutingName}
                                                    permissions={permissions}
                                                    isEditMode={isEditMode}
                                                    onChange={handleFieldChange}
                                                    setOnMatched={(val) => setOnMatched(selectedRoutingName, val)}
                                                    setOnUnMatched={(val) => setOnUnMatched(selectedRoutingName, val)}
                                                />
                                            );
                                        case "check_days":
                                            return (
                                                <DaysComponent
                                                    routingList={routingMap}
                                                    data={routing}
                                                    routingName={selectedRoutingName}
                                                    permissions={permissions}
                                                    isEditMode={isEditMode}
                                                    onChange={handleFieldChange}
                                                    setOnMatched={(val) => setOnMatched(selectedRoutingName, val)}
                                                    setOnUnMatched={(val) => setOnUnMatched(selectedRoutingName, val)}
                                                />
                                            );
                                        case "check_variable":
                                            return (
                                                <CheckVariableComponent
                                                    data={routing}
                                                    routingName={selectedRoutingName}
                                                    permissions={permissions}
                                                    isEditMode={isEditMode}
                                                    onChange={handleFieldChange}
                                                    getData={(...args) => {
                                                        const [routingName, field, value] = args;
                                                        if (field.includes("OnMatched"))
                                                            setOnMatched(routingName, value);
                                                        else if (field.includes("OnUnMatched"))
                                                            setOnUnMatched(routingName, value);
                                                        else handleFieldChange(routingName, field, value);
                                                    }}
                                                    routingList={routingMap}
                                                />
                                            );
                                        default:
                                            return null;
                                    }
                                })()}
                            </>
                        );
                    })()}
                </div>
            </div>
            </div>

            {routingToDelete && (
                <ConfirmAlert
                    open={!!routingToDelete}
                    name={routingToDelete}
                    onConfirm={() => deleteRouting(routingToDelete)}
                    onCancel={() => setRoutingToDelete(null)}
                />
            )}
        </>
    );
}
